import { useFile } from "@/shared/domain/files/hooks";
import { useLayout } from "@/shared/domain/layouts/hooks";
import { useTopicById } from "@/shared/domain/topics/hooks/useTopic";
import {
  ConfirmationAlert,
  ConfirmationCard,
  ConfirmationField,
  ConfirmationFields,
  ConfirmationPanel,
} from "@/shared/foundation/primitives";
import { nanoSecToSecStr } from "@/shared/time";

import { SignalList } from "./SignalList";
import { usePanelLabel } from "./usePanelLabel";
import type { VisualizeProposal } from "./visualizeProposal";

/**
 * Each lookup lives in its own component so it runs only for a proposal that
 * carries that id — a seek call has no business resolving a file service.
 * Each renders the id until its record lands, and keeps the id if it never
 * does.
 */
function TopicLabel({ topicId }: { topicId: string }) {
  const topic = useTopicById(topicId);
  return <>{topic.data?.topic_name ?? topicId}</>;
}

function FileLabel({ fileId }: { fileId: string }) {
  const file = useFile(fileId);
  return <>{file.data?.relative_path ?? fileId}</>;
}

function LayoutLabel({ layoutId }: { layoutId: string }) {
  const layout = useLayout(layoutId);
  return <>{layout.data?.name ?? layoutId}</>;
}

/**
 * Read-only preview of a visualizer tool call.
 *
 * These tools only change what is on screen — nothing here writes to the
 * platform — so the card says what will appear or disappear rather than
 * itemizing arguments. Ids are resolved to the names the user reads in the
 * assistant's own prose, but never at the cost of making them wait: an
 * unresolved id renders as itself and is replaced in place when its lookup
 * lands, so Approve is live and correctly placed from the first paint.
 */
export function VisualizeConfirmation({
  proposal,
}: {
  proposal: VisualizeProposal;
}) {
  const panel = usePanelLabel(proposal.panelId);

  // A panel id absent from the open workspace fails in `execute`. The model
  // chose it from a context snapshot that may predate the user closing it.
  const problem =
    proposal.problem ??
    (panel.knownMissing
      ? "That panel is no longer open in the visualizer."
      : undefined);

  if (problem !== undefined) {
    return (
      <ConfirmationPanel label={proposal.label}>
        <ConfirmationAlert>
          This request won&rsquo;t succeed. {problem}
        </ConfirmationAlert>
      </ConfirmationPanel>
    );
  }

  const {
    title,
    label,
    panelId,
    messagePathIds,
    syntheticTargets,
    changes,
    topicId,
    fileId,
    layoutId,
    timestampNs,
  } = proposal;

  return (
    <ConfirmationPanel label={label}>
      <ConfirmationCard title={title}>
        <ConfirmationFields>
          {panelId !== undefined && (
            <ConfirmationField label="Panel">
              {panel.label ?? panelId}
            </ConfirmationField>
          )}

          {topicId !== undefined && (
            <ConfirmationField label="Topic">
              <TopicLabel topicId={topicId} />
            </ConfirmationField>
          )}

          {fileId !== undefined && (
            <ConfirmationField label="File">
              <FileLabel fileId={fileId} />
            </ConfirmationField>
          )}

          {layoutId !== undefined && (
            <ConfirmationField label="Layout">
              <LayoutLabel layoutId={layoutId} />
            </ConfirmationField>
          )}

          {messagePathIds !== undefined && messagePathIds.length > 0 && (
            <ConfirmationField label={`Signals (${messagePathIds.length})`}>
              <SignalList messagePathIds={messagePathIds} />
            </ConfirmationField>
          )}

          {syntheticTargets !== undefined && syntheticTargets.length > 0 && (
            <ConfirmationField label={`Columns (${syntheticTargets.length})`}>
              {syntheticTargets.join(", ")}
            </ConfirmationField>
          )}

          {changes !== undefined && changes.length > 0 && (
            <ConfirmationField label="Changes">
              {changes.join("; ")}
            </ConfirmationField>
          )}

          {timestampNs !== undefined && (
            <ConfirmationField label="At">
              {nanoSecToSecStr(BigInt(timestampNs))} s
            </ConfirmationField>
          )}
        </ConfirmationFields>
      </ConfirmationCard>
    </ConfirmationPanel>
  );
}
